import { normalizeConfig } from './config_schema';
import { generateCodeGs } from './generate_code_gs';
import { generatePageHtml } from './generate_page_html';
import { hasEnabledPortalFields } from './portal_fields';

const PREVIEW_CODE_BANNER = /^\/\/ (Live preview|Boilerplate preview)[^\n]*\n+/;
const PREVIEW_HTML_BANNER = /^<!-- (Live preview|Boilerplate preview)[^\n]* -->\n+/;
const BUILDER_CODE_FOOTER =
  /\n*\/\/ Deploy: use Portal Builder deploy guide[^\n]*\n\/\/ After editing this file:[^\n]*\n?$/;

/**
 * Remove UI-only lines that must not ship in Code.gs / Page.html.
 * @param {string} codeGs
 */
export function stripPortalBuilderChromeFromCodeGs(codeGs) {
  return String(codeGs || '')
    .replace(PREVIEW_CODE_BANNER, '')
    .replace(BUILDER_CODE_FOOTER, '\n')
    .trimEnd();
}

/**
 * @param {string} pageHtml
 */
export function stripPortalBuilderChromeFromPageHtml(pageHtml) {
  return String(pageHtml || '').replace(PREVIEW_HTML_BANNER, '').trimEnd();
}

/**
 * True when config has real headers, lookup column, and page title, ready to deploy.
 * @param {import('./config_schema.js').PortalConfig} config
 */
export function isDeployReadyConfig(config) {
  const c = normalizeConfig(structuredClone(config));
  const namedColumns = (c.sheet.columns || []).filter((col) => String(col.header || '').trim());
  if (namedColumns.length === 0) return false;

  const lookupIdx =
    typeof c.sheet.lookup?.columnIndex === 'number' ? c.sheet.lookup.columnIndex : -1;
  if (lookupIdx < 0 || lookupIdx >= (c.sheet.columns?.length || 0)) return false;

  if (!String(c.script?.pageTitle || '').trim()) return false;

  if (!hasEnabledPortalFields(c)) return false;

  return true;
}

/**
 * Code.gs and Page.html exactly as they should be pasted into Apps Script (no builder chrome).
 * @param {import('./config_schema.js').PortalConfig} config
 * @returns {{ codeGs: string, pageHtml: string } | null}
 */
export function generateDeployArtifacts(config) {
  if (!isDeployReadyConfig(config)) return null;

  const c = normalizeConfig(structuredClone(config));
  c.script.htmlFileName = 'Page';
  if (!String(c.script?.webAppUrlPlaceholder || '').trim()) {
    c.script.webAppUrlPlaceholder = 'PASTE_DEPLOYMENT_URL_HERE';
  }

  const lookupCol = c.sheet.columns[c.sheet.lookup.columnIndex] || c.sheet.columns[0];
  const ref = c.sheet.defaultReference === 'letter' ? 'letter' : 'header';
  c.sheet.lookup = {
    ...c.sheet.lookup,
    reference: ref,
    header: lookupCol.header,
    letter: lookupCol.letter,
    number: lookupCol.number,
    columnIndex: c.sheet.lookup.columnIndex
  };

  const codeGs = stripPortalBuilderChromeFromCodeGs(
    generateCodeGs(c, { includeBuilderComments: false })
  );
  const pageHtml = stripPortalBuilderChromeFromPageHtml(generatePageHtml(c));

  if (!codeGs || !pageHtml) return null;

  return { codeGs, pageHtml };
}
